import _ from 'lodash'
import md5 from 'md5'

import {groupNameMaxLength} from '../../constants/NewTest'

const COPY_NAME_PREFIX = 'Copy of '

/**
 * Name for a duplicate of `originalName`: the `Copy of ` prefix plus a
 * numbered suffix (` (2)`, ` (3)`, …) while the name is already taken,
 * compared case-insensitively like the modal's own name-uniqueness rule.
 * The base is sliced so the suffix always survives, keeping the result
 * within `maxLength` (what the group-name input accepts) and unique.
 */
export const buildCopyName = (originalName, existingNames, maxLength = groupNameMaxLength) => {
  const taken = new Set(existingNames.map(name => name.trim().toLowerCase()))
  const base = `${COPY_NAME_PREFIX}${originalName}`.trim()

  let candidate = base

  for (let attempt = 1; attempt <= existingNames.length + 1; attempt += 1) {
    const suffix = attempt === 1 ? '' : ` (${attempt})`
    candidate = `${base.slice(0, maxLength - suffix.length).trimEnd()}${suffix}`

    if (!taken.has(candidate.toLowerCase())) {
      return candidate
    }
  }

  // Guard return. Attempts are not *proven* distinct — a name ending in the
  // very suffix the next attempt appends can collide once — but exhausting the
  // loop needs every attempt taken, and the truncated base would have to end in
  // both ` (n)` and ` of ` at the same offset. Return the last candidate rather
  // than reason about it: the caller's own name-uniqueness check is the backstop.
  return candidate
}

/**
 * Sort key for a qualifier's selected options. Composite on purpose: `precode` is
 * NULL for every option of `age` (in BOTH catalogs), and of `gender`, `race`,
 * `household_income`, `county` and `region` in the default catalog. `_.sortBy` is
 * stable, so a precode-only key silently left those in the creator's click order
 * — two groups selecting exactly the same people hashed differently and sailed
 * through the checkout duplicate gate. Falling back through `option` (the machine
 * key) and `displayName` keeps the order deterministic even if a catalog omits one
 * of the three.
 */
const subDemographicSortKey = subDemographic =>
  [
    subDemographic.option || '',
    subDemographic.precode || '',
    subDemographic.displayName || '',
  ].join('|')

/**
 * Identity of a group for duplicate detection. Groups with equal keys are
 * "the same" under the given rule. `name` and `daysInField` are excluded from
 * every rule.
 *
 * One function, two rules (below), so the two duplicate checks in this
 * feature cannot drift apart by accident.
 *
 * `sortSubDemographics` decides whether the qualifiers' own options are
 * compared as a set or as a list. Qualifier order is always normalized; each
 * `subDemographics` array is in the creator's click order (react-select
 * appends), so without this a group whose options were merely re-picked in a
 * different order hashes differently while selecting exactly the same people.
 */
export const getGroupIdentityKey = (
  group,
  {withLocale = false, withPanelists = false, sortSubDemographics = false} = {},
) => {
  const demographics = _.sortBy(group.demographics, 'key')
  const selections = sortSubDemographics
    ? demographics.map(demographic => ({
        ...demographic,
        subDemographics: _.sortBy(demographic.subDemographics, subDemographicSortKey),
      }))
    : demographics

  return [
    withLocale ? (group.locale && group.locale.value) || '' : '',
    md5(JSON.stringify(selections)),
    withPanelists ? group.totalCompletes : '',
  ].join('|')
}

/**
 * The two rules the story mandates. They are deliberately NOT the same, and
 * neither can be widened to match the other without breaking a written AC:
 *
 * - `CREATE_RULE` — the selection modal's check when a group is being created.
 *   Demographic selections only, and NOT sub-demographic-sorted, which is
 *   byte-for-byte what that check has always compared ("the existing check that
 *   blocks manually creating a group identical to one that already exists stays
 *   exactly as it works today"). Adding the locale, the panelist count or the
 *   sort would each make it reject a pair that group creation allows today.
 * - `CHECKOUT_RULE` — the audience step's Next and the order summary's publish
 *   gate ("identical demographic selections ... and the same total panelists"),
 *   so two groups differing only in panelist count are deliberately allowed.
 *   It sorts sub-demographics so that opening a copy and merely un-picking and
 *   re-picking an option — which permutes the array without changing the
 *   audience — cannot slip an identical pair through. The modal's save-time check
 *   guards the edit path too (restored 2026-08-18), but it compares selections
 *   only, so this remains the sole gate that also weighs the panelist count.
 */
export const CREATE_RULE = {withLocale: false, withPanelists: false, sortSubDemographics: false}
export const CHECKOUT_RULE = {withLocale: true, withPanelists: true, sortSubDemographics: true}

/**
 * First pair of matching groups as `[groupA, groupB]`, or `null` when no
 * two groups match. Only the first pair is reported: fixing it re-runs
 * the check, surfacing the next pair if there is one.
 */
export const findMatchingGroups = groups => {
  const seen = new Map()

  for (let i = 0; i < groups.length; i++) {
    const key = getGroupIdentityKey(groups[i], CHECKOUT_RULE)
    const previousGroup = seen.get(key)

    if (previousGroup) {
      return [previousGroup, groups[i]]
    }

    seen.set(key, groups[i])
  }

  return null
}

/**
 * `AudienceErrorModal` copy for a matching pair. It names the total panelists
 * as well as the selections — the create-time message deliberately says only
 * "selections", because that is all `CREATE_RULE` compares. Both groups are
 * named since at checkout there is no "current" group to infer.
 */
export const getMatchingGroupsMessage = ([groupA, groupB]) => ({
  text: `The selections and total panelists for "${groupB.name}" match exactly with "${groupA.name}".`,
  secondaryText: 'Please change at least one selection or the total panelists to continue.',
})
